import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import config from "../../config";
import makeRequest from "../../makeRequest";

/* ─────────────────────── Service Card ─────────────────────── */
const fallbackIcons = [
  "flaticon-stethoscope",
  "flaticon-hospital",
  "flaticon-first-aid",
  "flaticon-heart-1",
  "flaticon-ambulance",
  "flaticon-doctor",
];

const ServiceCard = ({ service, index }) => {
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (!service.hasImage) return;
    fetch(`${config.api.baseUrl}/website/service/${service.id}/image`)
      .then((r) => {
        if (!r.ok) throw new Error("no image");
        return r.blob();
      })
      .then((blob) => setImgSrc(URL.createObjectURL(blob)))
      .catch(() => {});
  }, [service.id, service.hasImage]);

  const icon = fallbackIcons[index % fallbackIcons.length];

  return (
    <div className="col-xl-4 col-lg-4 col-md-6 wow animate__fadeInUp" data-wow-duration=".8s" data-wow-delay={`${0.1 + (index % 6) * 0.08}s`}>
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          border: "1px solid #eef3fb",
          transition: "transform .25s, box-shadow .25s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-6px)";
          e.currentTarget.style.boxShadow = "0 16px 48px rgba(27,116,187,0.16)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.07)";
        }}
      >
        {/* Image or gradient header */}
        <div
          style={{
            height: "180px",
            overflow: "hidden",
            position: "relative",
            flexShrink: 0,
          }}
        >
          {imgSrc ? (
            <img src={imgSrc} alt={service.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                background: `linear-gradient(135deg, ${
                  ["#0e1c3d,#1b74bb", "#0d4f8a,#1b74bb", "#1b74bb,#3a9bd5", "#0e1c3d,#1b40a0", "#1b40a0,#1b74bb", "#0d4f8a,#3a9bd5"][index % 6]
                })`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className={icon} style={{ fontSize: "52px", color: "rgba(255,255,255,0.85)" }} />
            </div>
          )}
          {/* Overlay gradient at bottom for blending */}
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "40%",
              background: "linear-gradient(to top, rgba(14,28,61,0.2) 0%, transparent 100%)",
            }}
          />
        </div>

        {/* Content */}
        <div style={{ padding: "24px 24px 28px", display: "flex", flexDirection: "column", flex: 1 }}>
          {/* Icon badge */}
          <div
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #e8f2fb 0%, #d0e7f7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "14px",
            }}
          >
            <i className={icon} style={{ fontSize: "20px", color: "#1b74bb" }} />
          </div>

          <h5
            style={{
              fontSize: "17px",
              fontWeight: 700,
              color: "#0e1c3d",
              lineHeight: 1.35,
              marginBottom: "10px",
            }}
          >
            {service.name}
          </h5>

          {service.description && (
            <p
              style={{
                fontSize: "14px",
                lineHeight: 1.75,
                color: "#777",
                margin: 0,
                flex: 1,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {service.description}
            </p>
          )}

          {/* Bottom bar */}
          <div
            style={{
              marginTop: "20px",
              paddingTop: "16px",
              borderTop: "1px solid #eef3fb",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                width: "28px",
                height: "3px",
                background: "#1b74bb",
                borderRadius: "2px",
                display: "inline-block",
              }}
            />
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#1b74bb", letterSpacing: "1px", textTransform: "uppercase" }}>
              Our Service
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── Main Component ─────────────────────── */
const CeoMessageMain = ({ asSection = false }) => {
  const Tag = asSection ? "div" : "main";
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  useEffect(() => {
    makeRequest({ route: "/website/service/public", method: "get" })
      .then((data) => {
        setServices(data?.data || []);
        setServicesLoading(false);
      })
      .catch(() => setServicesLoading(false));
  }, []);

  return (
    <Tag>
      {!asSection && <Breadcrumb title="CEO's Message" subTitle="CEO's Message" />}

      {/* ── Main CEO Section ──────────────────────────── */}
      <section className="pt-120 pb-100">
        <div className="container">
          <div className="row g-5 align-items-start">
            {/* ── Portrait Column ── */}
            <div className="col-xl-4 col-lg-5 wow animate__fadeInLeft" data-wow-duration=".9s" data-wow-delay=".3s">
              <div style={{ position: "relative", paddingBottom: "36px" }}>
                {/* Decorative offset background block */}
                <div
                  style={{
                    position: "absolute",
                    top: "24px",
                    left: "-24px",
                    width: "88%",
                    height: "92%",
                    background: "linear-gradient(135deg, #0e1c3d 0%, #1b40a0 100%)",
                    borderRadius: "16px",
                    zIndex: 0,
                  }}
                />

                {/* Decorative corner accent */}
                <div
                  style={{
                    position: "absolute",
                    top: "-12px",
                    right: "10px",
                    width: "60px",
                    height: "60px",
                    borderTop: "4px solid #1b74bb",
                    borderRight: "4px solid #1b74bb",
                    borderRadius: "0 8px 0 0",
                    zIndex: 3,
                    opacity: 0.4,
                  }}
                />

                {/* Photo frame */}
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
                  }}
                >
                  <img
                    src="/images/ceo.png"
                    alt="CEO – Maroof International Hospital"
                    style={{ width: "100%", display: "block", objectFit: "cover", maxHeight: "500px" }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: "30%",
                      background: "linear-gradient(to top, rgba(14,28,61,0.55) 0%, transparent 100%)",
                    }}
                  />
                </div>

                {/* Nameplate card */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "24px",
                    right: "8px",
                    background: "#fff",
                    borderRadius: "12px",
                    padding: "16px 22px",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.14)",
                    zIndex: 2,
                    borderLeft: "5px solid #1b74bb",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "2px",
                      textTransform: "uppercase",
                      color: "#1b74bb",
                      marginBottom: "4px",
                    }}
                  >
                    Chief Executive Officer
                  </p>
                  <h5
                    style={{
                      margin: 0,
                      fontSize: "17px",
                      fontWeight: 800,
                      color: "#0e1c3d",
                      lineHeight: 1.25,
                    }}
                  >
                    Maroof International Hospital
                  </h5>
                </div>
              </div>
            </div>

            {/* ── Message Column ── */}
            <div
              className="col-xl-8 col-lg-7 wow animate__fadeInRight"
              data-wow-duration=".9s"
              data-wow-delay=".4s"
              style={{ paddingLeft: "clamp(16px, 3vw, 48px)" }}
            >
              {/* Section subtitle tag */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
                <span
                  style={{
                    display: "inline-block",
                    width: "36px",
                    height: "3px",
                    background: "#1b74bb",
                    borderRadius: "2px",
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "2.5px",
                    textTransform: "uppercase",
                    color: "#1b74bb",
                  }}
                >
                  Leadership &amp; Vision
                </span>
              </div>

              {/* Heading */}
              <h2
                style={{
                  fontSize: "clamp(26px, 3.2vw, 40px)",
                  fontWeight: 800,
                  color: "#0e1c3d",
                  lineHeight: 1.2,
                  marginBottom: "28px",
                }}
              >
                A Message from <span style={{ color: "#1b74bb" }}>Our CEO</span>
              </h2>

              {/* Quote pull-out */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "16px",
                  marginBottom: "28px",
                  padding: "20px 24px",
                  background: "linear-gradient(135deg, #e8f2fb 0%, #f0f7ff 100%)",
                  borderRadius: "12px",
                  borderLeft: "4px solid #1b74bb",
                }}
              >
                <i
                  className="fas fa-quote-left"
                  style={{ fontSize: "28px", color: "#1b74bb", opacity: 0.7, flexShrink: 0, marginTop: "2px" }}
                />
                <p
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#0e1c3d",
                    lineHeight: 1.7,
                    fontStyle: "italic",
                  }}
                >
                  MIH is all about people with compassionate hearts, and people with a purpose to serve the community well — delivering on that
                  promise every single day.
                </p>
              </div>

              {/* Message paragraphs */}
              <p style={{ fontSize: "15.5px", lineHeight: 1.9, color: "#555", marginBottom: "20px" }}>
                We are committed to meeting the healthcare needs of the community. The doctors, nurses, and other caregivers who truly transform
                the lives of their humans, combining the wonders of <strong>compassion along with the wonders of technology</strong> every day.
                Here at MIH, we are constantly growing and evolving to meet the needs of our vibrant community, and our vision is to be the{" "}
                <strong>hospital of choice for patients, physicians and staff</strong> in the region. To maintain our lead, we work effortlessly
                and invest in new services, providers and modern technology.
              </p>

              <p style={{ fontSize: "15.5px", lineHeight: 1.9, color: "#555", marginBottom: "20px" }}>
                COVID-19 hit us hard and stayed longer than we were expecting. It has made a difference in this community and around the world.
                Looking back on the Pandemic, <strong>Maroof International Hospital</strong> had met the grand challenges that ultimately
                sharpened our focus and brought us together. Our staff and doctors rolled up their sleeves — we faced everyday obstacles with{" "}
                <strong>courage and determination</strong>.
              </p>

              <p style={{ fontSize: "15.5px", lineHeight: 1.9, color: "#555", marginBottom: "20px" }}>
                We are a trusted source for helping you and your family's health all year round. We feel a deep sense of pride and devotion to
                all those who trust our care. We recognize our responsibility to provide the <strong>best medical care available</strong>, and
                we take unwavering pride that our staff and doctors deliver on that promise every day. We look forward to shaping the future and
                embracing new changes to expand our services and keep your care homegrown.
              </p>

              <p style={{ fontSize: "15.5px", lineHeight: 1.9, color: "#555", marginBottom: "32px" }}>
                I extend my heartfelt gratitude to all the healthcare providers including the <strong>clinical and non-clinical staff</strong>{" "}
                as well as our external partners for their dedication and devotion in enabling us to stand tall in the healthcare sector.
              </p>

              {/* Signature block */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  paddingTop: "24px",
                  borderTop: "1px solid #e2ecf7",
                }}
              >
                <div
                  style={{
                    width: "54px",
                    height: "54px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #1b74bb 0%, #0d4f8a 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 6px 20px rgba(27,116,187,0.35)",
                  }}
                >
                  <i className="flaticon-hospital" style={{ color: "#fff", fontSize: "22px" }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "#0e1c3d", lineHeight: 1.3 }}>Chief Executive Officer</p>
                  <p style={{ margin: 0, fontSize: "13.5px", color: "#1b74bb", fontWeight: 500 }}>Maroof International Hospital, Islamabad</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Divider ──────────────────────────────────────────── */}
      <div style={{ background: "#f7f9fc", height: "2px", margin: "0" }} />

      {/* ── Our Services Section ─────────────────────────────── */}
      <section style={{ background: "#f7f9fc", padding: "96px 0 100px" }}>
        <div className="container">
          {/* Section header */}
          <div className="row justify-content-center mb-60">
            <div className="col-xl-7 col-lg-9 text-center wow animate__fadeInUp" data-wow-duration=".9s" data-wow-delay=".2s">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "14px" }}>
                <span style={{ display: "inline-block", width: "36px", height: "3px", background: "#1b74bb", borderRadius: "2px" }} />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "2.5px",
                    textTransform: "uppercase",
                    color: "#1b74bb",
                  }}
                >
                  What We Offer
                </span>
                <span style={{ display: "inline-block", width: "36px", height: "3px", background: "#1b74bb", borderRadius: "2px" }} />
              </div>
              <h2
                style={{
                  fontSize: "clamp(26px, 3.2vw, 40px)",
                  fontWeight: 800,
                  color: "#0e1c3d",
                  lineHeight: 1.25,
                  marginBottom: "16px",
                }}
              >
                Our <span style={{ color: "#1b74bb" }}>Medical Services</span>
              </h2>
              <p style={{ fontSize: "15.5px", lineHeight: 1.8, color: "#666", margin: 0 }}>
                Maroof International Hospital offers round-the-clock tertiary care with international-standard operation theaters, specialized
                care units and state-of-the-art diagnostics — all under one roof.
              </p>
            </div>
          </div>

          {/* Services grid */}
          {servicesLoading ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  border: "4px solid #e2ecf7",
                  borderTopColor: "#1b74bb",
                  borderRadius: "50%",
                  margin: "0 auto",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : services.length === 0 ? null : (
            <div className="row g-4">
              {services.map((service, i) => (
                <ServiceCard key={service.id} service={service} index={i} />
              ))}
            </div>
          )}

          {/* Bottom CTA */}
          {services.length > 0 && (
            <div className="row justify-content-center mt-60 wow animate__fadeInUp" data-wow-duration=".9s" data-wow-delay=".3s">
              <div className="col-auto text-center">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "32px",
                    background: "linear-gradient(135deg, #0e1c3d 0%, #1b74bb 100%)",
                    borderRadius: "16px",
                    padding: "28px 48px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.65)",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        marginBottom: "4px",
                      }}
                    >
                      Available Around the Clock
                    </p>
                    <p style={{ margin: 0, fontSize: "22px", fontWeight: 800, color: "#fff" }}>24 / 7 Emergency Care</p>
                  </div>
                  <a
                    href="/appointment"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "10px",
                      background: "#fff",
                      color: "#0e1c3d",
                      fontWeight: 700,
                      fontSize: "14px",
                      padding: "14px 28px",
                      borderRadius: "10px",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                      transition: "background .2s, color .2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#1b74bb";
                      e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#0e1c3d";
                    }}
                  >
                    <i className="flaticon-calendar" style={{ fontSize: "16px" }} />
                    Book an Appointment
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </Tag>
  );
};

export default CeoMessageMain;
